import React, { useCallback } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Select, MenuItem, Typography, Paper, Divider, IconButton, Tooltip, FormControl, InputLabel, Box, Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import moment from 'moment';
import constants from '../../utilities/constants';
import debounce from 'lodash/debounce';
import { StateManager } from '../../utilities/stateManager';

const PartEditForm = ({ part, onUpdate, onCancel, onSubmit }) => {
  const statusOptions = constants.part_statuses
    .filter(status => status !== 'complete')
    .map(status => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1)
    }));

  // Debounced update function
  const debouncedUpdate = useCallback(
    debounce((updates) => {
      onUpdate(updates);
    }, 300),
    [onUpdate]
  );

  // Handle text field changes with debouncing
  const handleTextChange = (field) => (e) => {
    const value = e.target.value;
    // Update the local state immediately for responsive UI
    e.target.value = value;
    // Debounce the actual update
    debouncedUpdate({ [field]: value });
  };

  // Handle number field changes with debouncing
  const handleNumberChange = (field) => (e) => {
    const value = parseFloat(e.target.value) || 0;
    // Update the local state immediately for responsive UI
    e.target.value = value;
    // Debounce the actual update
    debouncedUpdate({ [field]: value });
  };

  // Handle select changes immediately (no debounce needed)
  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    const updates = { [field]: value };
    
    if (field === 'status') {
      // Always update status_time when status changes
      updates.status_time = moment().format('YYYY/MM/DD');
    }
    onUpdate(updates);
  };

  // Handle copy ID to clipboard
  const handleCopyId = () => {
    navigator.clipboard.writeText(part.id);
  };

  // Handle date changes
  const handleDateChange = (field) => (date) => {
    const formattedDate = date ? moment(date).format('YYYY/MM/DD') : null;
    onUpdate({ [field]: formattedDate });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  Part Details
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Copy Part ID">
                    <IconButton onClick={handleCopyId} size="small">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton onClick={onCancel} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          {/* Image and Vehicle Info Section */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    height: 200, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'action.hover',
                    borderRadius: 1
                  }}
                >
                  {part.car_thumbnail ? (
                    <img 
                      src={part.car_thumbnail} 
                      alt="Vehicle" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                  ) : (
                    <DirectionsCarIcon sx={{ fontSize: 80, color: 'action.disabled' }} />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Service Order Number"
                      value={part.order || ''}
                      InputProps={{
                        readOnly: true,
                        sx: { 
                          '& .MuiInputBase-input': { 
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Vehicle Information
                    </Typography>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        backgroundColor: 'action.hover',
                        borderRadius: 1
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Stock Number
                          </Typography>
                          <Typography variant="body1">
                            {part.stockNumber || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Year
                          </Typography>
                          <Typography variant="body1">
                            {part.year || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Make
                          </Typography>
                          <Typography variant="body1">
                            {part.make || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Model
                          </Typography>
                          <Typography variant="body1">
                            {part.model || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Trim
                          </Typography>
                          <Typography variant="body1">
                            {part.trim || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Part Name"
                  defaultValue={part.name || ''}
                  onChange={handleTextChange('name')}
                  InputProps={{ 
                    style: { fontSize: 18, fontWeight: 'bold' },
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vendor"
                  defaultValue={part.vendor || ''}
                  onChange={handleTextChange('vendor')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vendor Link"
                  defaultValue={part.link || ''}
                  onChange={handleTextChange('link')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={part.status || 'pending'}
                    onChange={handleSelectChange('status')}
                    sx={{ 
                      '& .MuiSelect-select': { py: 1.5 },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  defaultValue={part.location || ''}
                  onChange={handleTextChange('location')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Dates Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Dates
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Order Date"
                  value={part.orderDate ? moment(part.orderDate) : null}
                  onChange={handleDateChange('orderDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Est. Arrival Date"
                  value={part.arrivalDate ? moment(part.arrivalDate) : null}
                  onChange={handleDateChange('arrivalDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Return Date"
                  value={part.returnDate ? moment(part.returnDate) : null}
                  onChange={handleDateChange('returnDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Financial Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Financial Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cost"
                  defaultValue={part.cost || 0}
                  onChange={handleNumberChange('cost')}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={onCancel}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={onSubmit}
                sx={{ minWidth: 100 }}
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
};

export default PartEditForm; 